'use client';

import 'BomdisoftComponents/inputs/file-drop/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { UploadRoundedIcon } from 'BomdisoftComponents/icons/upload-rounded';
import { type ChangeEvent, type DragEvent, useId, useRef, useState } from 'react';

export type FileDropProps = {
  accept?: string;
  actionLabel: string;
  className?: string;
  disabled?: boolean;
  hint?: string;
  label: string;
  onFile: (file: File) => void;
  testId?: string;
};

export const FileDrop = ({ accept, actionLabel, className, disabled = false, hint, label, onFile, testId }: FileDropProps) => {
  const hintId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [isOver, setIsOver] = useState(false);

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!disabled) setIsOver(true);
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsOver(false);
    const file = event.dataTransfer?.files?.[0];
    if (!disabled && file !== undefined) onFile(file);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file !== undefined) onFile(file);
    event.target.value = '';
  };

  return (
    <div className={joinClasses('bomdisoft-file-drop', className)} data-testid={testId}>
      <button
        aria-describedby={hint ? hintId : undefined}
        className='bomdisoft-file-drop-zone'
        data-over={isOver ? '' : undefined}
        data-testid={testId ? `${testId}-zone` : undefined}
        disabled={disabled}
        onClick={() => input.current?.click()}
        onDragLeave={() => setIsOver(false)}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        type='button'
      >
        <UploadRoundedIcon size={22} />
        <span className='bomdisoft-file-drop-label'>{label}</span>
        <span className='bomdisoft-file-drop-action'>{actionLabel}</span>
      </button>
      <input accept={accept} className='bomdisoft-file-drop-input' data-testid={testId ? `${testId}-input` : undefined} disabled={disabled} hidden onChange={handleChange} ref={input} type='file' />
      {hint ? (
        <p className='bomdisoft-file-drop-hint' id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
};
